import type { ElementRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MetadataService } from '../../../services/metadata/metadata';
import { HighlightsService } from '../../../services/selections/highlights';

import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslatePipe } from '@ngx-translate/core';
import type { OdlInput, OdlPopover } from '@odilo/ui-components';
import { TextSearchService } from '../../../services/text-search/text-search';
import { SearchResultsComponent } from '../search-results/search-results';

@Component({
  selector: 'nr-search',
  imports: [SearchResultsComponent, TranslatePipe],
  templateUrl: './search-bar.html',
  styleUrl: './search-bar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SearchBarComponent {
  private readonly dragHandler = this.drag.bind(this);
  private readonly dragEndHandler = this.dragEnd.bind(this);

  private readonly searchPopover = viewChild.required<ElementRef<HTMLElement>>('searchPopover');
  private readonly dragHandle = viewChild.required<ElementRef<HTMLElement>>('dragHandle');
  private readonly popoverShadowEl = computed(
    () => this.searchPopover().nativeElement.shadowRoot!.querySelector<HTMLElement>('#popover')!,
  );

  private readonly metadataService = inject(MetadataService);
  private readonly textSearchService = inject(TextSearchService);
  private readonly highlightsService = inject(HighlightsService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  private activePointerId: number | undefined = undefined;
  private xOffset = 0;
  private yOffset = 0;
  private initialX = 0;
  private initialY = 0;
  private currentX = 0;
  private currentY = 0;

  readonly textToSearch = this.textSearchService.wordToSearch;
  readonly open = this.textSearchService.searchPopoverOpened;
  readonly positions = this.metadataService.positions;
  readonly searchResults = this.textSearchService.searchResults;
  readonly minCharsToSearch = this.textSearchService.minCharsToSearch;
  /** Translation key of the problem to show under the input, if any. */
  readonly errorKey = computed<string | undefined>(() => {
    if (this.textSearchService.isSearchTooShort()) return 'SEARCH_BAR.MIN_CHARACTERS';
    if (this.searchResults.error()) return 'SEARCH_BAR.ERROR';
    return undefined;
  });
  readonly totalSearchResults = computed(() =>
    this.textSearchService.searchResults.hasValue()
      ? this.textSearchService.searchResults.value().length
      : 0,
  );
  readonly showSearchIndex = computed(() => {
    if (this.textSearchService.indexSearch() == undefined) return 0;
    return this.textSearchService.indexSearch()! + 1;
  });
  readonly resultsModalOpened = signal(false);
  readonly resultsPopover = viewChild<ElementRef<OdlPopover>>('resultsPopover');
  readonly searchInput = viewChild.required<ElementRef<OdlInput>>('searchInput');
  readonly popoverFor = signal<string>('');

  readonly searchBarClosed = output();

  readonly isDragging = signal(false);

  constructor() {
    setTimeout(() => {
      this.breakpointObserver.observe('(min-width: 1200px)').subscribe((result) => {
        this.popoverFor.set(result.matches ? '#header-search-button' : '#footer-search-button');
      });
    });
  }

  onOpen(): void {
    this.textSearchService.searchPopoverOpened.set(true);
    this.resultsModalOpened.set(false);
    document.addEventListener('pointermove', this.dragHandler);
    document.addEventListener('pointerup', this.dragEndHandler);
    document.addEventListener('pointercancel', this.dragEndHandler);
  }

  onClose(): void {
    this.clearSearch();
    this.textSearchService.searchPopoverOpened.set(false);
    document.removeEventListener('pointermove', this.dragHandler);
    document.removeEventListener('pointerup', this.dragEndHandler);
    document.removeEventListener('pointercancel', this.dragEndHandler);
  }

  dragStart(e: PointerEvent): void {
    e.preventDefault();

    this.xOffset = parseFloat(this.popoverShadowEl().style.left);
    this.yOffset = parseFloat(this.popoverShadowEl().style.top);

    this.isDragging.set(true);
    this.activePointerId = e.pointerId;

    try {
      this.dragHandle().nativeElement.setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('setPointerCapture no disponible:', err);
    }

    this.initialX = e.clientX - this.xOffset;
    this.initialY = e.clientY - this.yOffset;
  }

  drag(e: PointerEvent): void {
    if (!this.isDragging() || this.activePointerId !== e.pointerId) {
      return;
    }

    e.preventDefault();

    this.currentX = e.clientX - this.initialX;
    this.currentY = e.clientY - this.initialY;

    this.xOffset = this.currentX;
    this.yOffset = this.currentY;

    const maxX = window.innerWidth - this.popoverShadowEl().offsetWidth;
    const maxY = window.innerHeight - this.popoverShadowEl().offsetHeight;

    this.xOffset = Math.max(0, Math.min(this.xOffset, maxX));
    this.yOffset = Math.max(0, Math.min(this.yOffset, maxY));
    SearchBarComponent.setTranslate(this.xOffset, this.yOffset, this.popoverShadowEl());
  }

  dragEnd(e: PointerEvent): void {
    if (!this.isDragging() || this.activePointerId !== e.pointerId) {
      return;
    }

    e.preventDefault();

    this.initialX = this.currentX;
    this.initialY = this.currentY;
    this.isDragging.set(false);
    this.activePointerId = undefined;
    try {
      if (e.pointerId !== -1) {
        this.dragHandle().nativeElement.releasePointerCapture(e.pointerId);
      }
    } catch (err) {
      console.warn('releasePointerCapture no disponible:', err);
    }
  }

  static setTranslate(xPos: number, yPos: number, el: HTMLElement): void {
    el.style.left = `${xPos}px`;
    el.style.top = `${yPos}px`;
  }

  searchText(textToSearch: string): void {
    if (!textToSearch.trim()) return;
    // Still set when too short: the service won't call the server, and the bar shows why.
    this.textSearchService.search(textToSearch.trim());
  }

  navigateToPreviousResult(): void {
    this.textSearchService.indexSearch.update((indexSearch) =>
      indexSearch != undefined
        ? (indexSearch - 1 + this.totalSearchResults()) % this.totalSearchResults()
        : undefined,
    );
  }

  navigateToNextResult(): void {
    this.textSearchService.indexSearch.update((indexSearch) =>
      indexSearch != undefined ? (indexSearch + 1) % this.totalSearchResults() : undefined,
    );
  }

  clearSearch(): void {
    this.textSearchService.clearSearchResults();
  }

  close(): void {
    this.clearSearch();
    this.searchInput().nativeElement.value = '';
    const popover = this.resultsPopover()?.nativeElement;
    if (popover) {
      this.toggleResultsModal();
      popover.open = false;
    }
    this.textSearchService.searchPopoverOpened.set(false);
  }

  toggleResultsModal(): void {
    this.resultsModalOpened.update((v) => !v);
  }
}
